import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHero from 'components/PageHero';
import { Filter, Spade, Users, Zap, RefreshCw, Rocket } from 'lucide-react';
import api, { markInAppNavigation, readRecentLeaveWalletBalance } from 'lib/api';
import { API_ENDPOINTS } from 'constants/game';
import { LobbyTable } from 'types/game';
import { useAuth } from 'context/AuthProvider';
import { formatCurrencyAmount } from 'lib/currency-format';
import { getFreeBalance, hasActiveTableChips } from 'lib/balance';
import { useTableJoin } from 'hooks/useTableJoin';
import { goBackInApp } from 'lib/navigation';
import { readLastPlayedTable } from 'lib/lastTableStorage';
import { pickQuickJoinTarget, quickJoinTargetLabel } from 'lib/quickJoin';
import { TABLE_STATUS_CLASS, TABLE_STATUS_LABEL, tableSeatStatus } from 'lib/tableStatus';
import { formatVariantLabel } from 'lib/format-variant';
import { applyVariantThemeCss, cacheVariantAssets, rememberLastVariant } from 'lib/variant-assets';
import PrivateRoomModal, { PrivateRoomLobbyCard } from 'components/lobby/PrivateRoomModal';
import { clearPendingRoomCode, readPendingRoomCode } from 'lib/pendingRoomCode';

type SeatsFilter = 'any' | 'open' | 'has_seats';
type SortOption = 'boot_asc' | 'boot_desc' | 'min_buy_in_asc' | 'seats_desc';

function tableMinBuyIn(table: LobbyTable): number {
  const explicit = Number(table.min_buy_in);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  return Number(table.boot_amount) || 0;
}

function applyLobbyFilters(
  tables: LobbyTable[],
  opts: {
    bootMin: number;
    bootMax: number;
    maxMinBuyIn: number | null;
    affordableOnly: boolean;
    seatsFilter: SeatsFilter;
    sortBy: SortOption;
    freeBalance: number;
  }
): LobbyTable[] {
  let list = [...tables];

  list = list.filter((t) => {
    const boot = Number(t.boot_amount) || 0;
    if (boot < opts.bootMin || boot > opts.bootMax) return false;

    const minBuyIn = tableMinBuyIn(t);
    if (opts.maxMinBuyIn != null && minBuyIn > opts.maxMinBuyIn) return false;
    if (opts.affordableOnly && minBuyIn > opts.freeBalance) return false;

    const status = tableSeatStatus(t);
    if (opts.seatsFilter === 'open' && status !== 'open') return false;
    if (opts.seatsFilter === 'has_seats' && status === 'full') return false;

    return true;
  });

  list.sort((a, b) => {
    switch (opts.sortBy) {
      case 'boot_desc':
        return Number(b.boot_amount) - Number(a.boot_amount);
      case 'min_buy_in_asc':
        return tableMinBuyIn(a) - tableMinBuyIn(b);
      case 'seats_desc':
        return Number(b.seated_players) - Number(a.seated_players);
      case 'boot_asc':
      default:
        return Number(a.boot_amount) - Number(b.boot_amount);
    }
  });

  return list;
}

export default function Lobby() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const variantParam = searchParams.get('variant') || 'Classic';
  const variantFilter = variantParam.toLowerCase();
  const privateSectionOpen = searchParams.get('private') === '1';
  const [pendingRoomCode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('roomCode') || '';
    const fromStorage = readPendingRoomCode() || '';
    const code = (fromQuery || fromStorage).trim().toUpperCase();
    if (fromStorage) clearPendingRoomCode();
    return code;
  });
  const { user, refreshSession, getUserSnapshot } = useAuth();
  const [allTables, setAllTables] = useState<LobbyTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [joiningTableId, setJoiningTableId] = useState<number | null>(null);
  const [quickJoining, setQuickJoining] = useState(false);
  const [bootMin, setBootMin] = useState(0);
  const [bootMax, setBootMax] = useState(Number.MAX_SAFE_INTEGER);
  const [maxMinBuyIn, setMaxMinBuyIn] = useState<number | null>(null);
  const [affordableOnly, setAffordableOnly] = useState(false);
  const [seatsFilter, setSeatsFilter] = useState<SeatsFilter>('any');
  const [sortBy, setSortBy] = useState<SortOption>('boot_asc');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [privateModalOpen, setPrivateModalOpen] = useState(
    () => privateSectionOpen || Boolean(pendingRoomCode)
  );

  const {
    joinLoading,
    error,
    setError,
    joinTable,
    navigateToTable,
  } = useTableJoin(refreshSession, getUserSnapshot);

  const currencySymbol = user?.currencySymbol ?? '';
  const currencyCode = user?.currency ?? null;
  const freeBalance = getFreeBalance(user);

  const fetchTables = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError('');
    try {
      const res = await api.get<LobbyTable[]>(API_ENDPOINTS.FRONTEND.LOBBY_TABLES);
      if (res.success && Array.isArray(res.data)) {
        const filtered = res.data.filter(
          (t) => !variantFilter || (t.variant_code || t.variant_name || '').toLowerCase().includes(variantFilter)
        );
        setAllTables(filtered);
      } else {
        setAllTables([]);
      }
    } catch {
      setError('Could not load tables — try again');
      setAllTables([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [setError, variantFilter]);

  useEffect(() => {
    markInAppNavigation();
    const leaveWallet = readRecentLeaveWalletBalance();
    void refreshSession({
      forceLiveWallet: true,
      ...(leaveWallet != null
        ? {
            clearedTable: true,
            minExpectedWallet: leaveWallet > 0 ? leaveWallet : undefined,
          }
        : {}),
    });
    void fetchTables();
    rememberLastVariant(variantParam);
    applyVariantThemeCss(variantParam);
    api.get(API_ENDPOINTS.FRONTEND.VARIANTS).then((res) => {
      if (res.success && Array.isArray(res.data)) cacheVariantAssets(res.data);
    });
  }, [fetchTables, refreshSession, variantParam]);

  const bootBounds = useMemo(() => {
    if (!allTables.length) return { min: 0, max: 0 };
    const boots = allTables.map((t) => Number(t.boot_amount) || 0);
    return { min: Math.min(...boots), max: Math.max(...boots) };
  }, [allTables]);

  useEffect(() => {
    if (allTables.length === 0) return;
    setBootMin(bootBounds.min);
    setBootMax(bootBounds.max);
  }, [allTables.length, bootBounds.min, bootBounds.max]);

  const tables = useMemo(
    () =>
      applyLobbyFilters(allTables, {
        bootMin,
        bootMax,
        maxMinBuyIn,
        affordableOnly,
        seatsFilter,
        sortBy,
        freeBalance,
      }),
    [allTables, bootMin, bootMax, maxMinBuyIn, affordableOnly, seatsFilter, sortBy, freeBalance]
  );

  const quickJoinTarget = useMemo(
    () =>
      pickQuickJoinTarget(
        allTables.filter(
          (t) =>
            !variantFilter ||
            (t.variant_code || t.variant_name || '').toLowerCase().includes(variantFilter)
        ),
        freeBalance,
        {
          activeTableProfileId: user?.activeTableProfileId,
          activeTableStack: user?.tableStack,
          lastPlayedTableProfileId: readLastPlayedTable(),
        }
      ),
    [allTables, variantFilter, freeBalance, user?.activeTableProfileId, user?.tableStack]
  );

  const handleTakeSeat = async (table: LobbyTable) => {
    const status = tableSeatStatus(table);
    if (status === 'full') {
      setError(`${table.name} is full — try another table or check back soon`);
      return;
    }

    if (hasActiveTableChips(user, table.id)) {
      navigateToTable(table.id);
      return;
    }

    setJoiningTableId(table.id);
    await joinTable(table.id);
    setJoiningTableId(null);
  };

  const handleQuickJoin = async () => {
    if (!quickJoinTarget) {
      setError('No affordable table with an open seat right now — adjust filters or add funds');
      return;
    }
    setQuickJoining(true);
    try {
      if (quickJoinTarget.skipBuyIn) {
        navigateToTable(quickJoinTarget.table.id);
      } else {
        await joinTable(quickJoinTarget.table.id);
      }
    } finally {
      setQuickJoining(false);
    }
  };

  const variantLabel = formatVariantLabel(variantParam);

  useEffect(() => {
    if (!privateSectionOpen && !pendingRoomCode) return;
    const el = document.getElementById('private-rooms');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [privateSectionOpen, pendingRoomCode, loading]);

  return (
    <div className="lobby-page flex flex-col gap-5 pb-2 md:pb-1">
      <PageHero
        eyebrow="Game lobby"
        title="Choose your table"
        showBack
        description={
          user
            ? `Welcome, ${user.username}. Each table is a different stakes tier — same ${variantLabel} rules, different boot and bet limits.`
            : `Pick a stakes tier and join a live ${variantLabel} table — your full wallet is brought to the table.`
        }
        icon={<Spade size={16} className="text-tp-gold" fill="currentColor" />}
      />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-white">Available tables</h2>
          <p className="mt-1 text-sm text-white/45">
            {variantLabel} · {tables.length} of {allTables.length} stake{allTables.length === 1 ? '' : 's'} tier{allTables.length === 1 ? '' : 's'} · Real-time matchmaking
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void handleQuickJoin()}
            disabled={!quickJoinTarget || quickJoining || joinLoading || loading}
            className="inline-flex items-center gap-2 rounded-xl border border-tp-gold/35 bg-tp-gold/15 px-4 py-2 text-sm font-bold text-tp-gold transition hover:bg-tp-gold/25 disabled:cursor-not-allowed disabled:opacity-45"
            title={
              quickJoinTarget
                ? `${quickJoinTargetLabel(quickJoinTarget)} · boot ${formatCurrencyAmount(currencySymbol, Number(quickJoinTarget.table.boot_amount), currencyCode)}`
                : 'No affordable open table'
            }
          >
            <Rocket size={16} />
            {quickJoining ? 'Finding seat…' : 'Quick join'}
          </button>
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10"
          >
            <Filter size={16} />
            Filters
          </button>
          <button
            type="button"
            onClick={() => void fetchTables(true)}
            disabled={refreshing || loading}
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10 disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {filtersOpen && (
        <div className="game-table-card mt-4 grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block text-sm">
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">Boot min</span>
            <input
              type="number"
              min={0}
              step={1}
              value={bootMin}
              onChange={(e) => setBootMin(Number(e.target.value) || 0)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-white"
            />
          </label>
          <label className="block text-sm">
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">Boot max</span>
            <input
              type="number"
              min={0}
              step={1}
              value={bootMax === Number.MAX_SAFE_INTEGER ? bootBounds.max : bootMax}
              onChange={(e) => setBootMax(Number(e.target.value) || 0)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-white"
            />
          </label>
          <label className="block text-sm">
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">Max min buy-in</span>
            <input
              type="number"
              min={0}
              step={1}
              placeholder="Any"
              value={maxMinBuyIn ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setMaxMinBuyIn(v === '' ? null : Number(v) || 0);
              }}
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-white"
            />
          </label>
          <label className="block text-sm">
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">Seats</span>
            <select
              value={seatsFilter}
              onChange={(e) => setSeatsFilter(e.target.value as SeatsFilter)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-white"
            >
              <option value="any">Any</option>
              <option value="open">Open only</option>
              <option value="has_seats">Has open seats</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">Sort by</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-white"
            >
              <option value="boot_asc">Boot (low → high)</option>
              <option value="boot_desc">Boot (high → low)</option>
              <option value="min_buy_in_asc">Min buy-in (low → high)</option>
              <option value="seats_desc">Most players seated</option>
            </select>
          </label>
          <label className="flex items-center gap-2 self-end text-sm text-white/75 sm:col-span-2">
            <input
              type="checkbox"
              checked={affordableOnly}
              onChange={(e) => setAffordableOnly(e.target.checked)}
              className="rounded border-white/20"
            />
            Only tables I can afford ({formatCurrencyAmount(currencySymbol, freeBalance, currencyCode)})
          </label>
        </div>
      )}

      {error && (
        <p className="mt-4 rounded-lg border border-red-500/30 bg-red-950/40 px-4 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      {loading ? (
        <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="game-table-card h-44 animate-pulse bg-white/5" />
          ))}
        </div>
      ) : tables.length === 0 ? (
        <div className="game-table-card mt-4 space-y-3 p-8 text-center text-white/55">
          <p>No {variantLabel} tables match your filters.</p>
          <button
            type="button"
            className="text-sm font-semibold text-tp-gold hover:underline"
            onClick={() => goBackInApp(navigate, '/home')}
          >
            Back to variants
          </button>
        </div>
      ) : (
        <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {tables.map((t) => {
            const status = tableSeatStatus(t);
            const isJoining = joiningTableId === t.id || (joinLoading && joiningTableId === t.id);
            const isFull = status === 'full';
            const minBuyIn = tableMinBuyIn(t);
            const isRejoin = hasActiveTableChips(user, t.id);
            const canAfford = isRejoin || minBuyIn <= freeBalance;
            return (
              <article key={t.id} className="game-table-card flex flex-col p-5">
                <div className="game-table-card-felt mb-4 rounded-full" />
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-display text-lg font-bold text-white">{t.name}</h3>
                    <p className="mt-0.5 text-xs uppercase tracking-wider text-emerald-400/70">
                      {t.variant_name || variantLabel}
                    </p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${TABLE_STATUS_CLASS[status]}`}>
                    {TABLE_STATUS_LABEL[status]}
                  </span>
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-[10px] font-bold uppercase tracking-wider text-white/35">Boot</dt>
                    <dd className="font-semibold tabular-nums text-tp-gold">
                      {formatCurrencyAmount(currencySymbol, Number(t.boot_amount), currencyCode)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-bold uppercase tracking-wider text-white/35">Min buy-in</dt>
                    <dd className={`font-semibold tabular-nums ${canAfford ? 'text-white/75' : 'text-red-300/80'}`}>
                      {formatCurrencyAmount(currencySymbol, minBuyIn, currencyCode)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-bold uppercase tracking-wider text-white/35">Bet range</dt>
                    <dd className="font-semibold tabular-nums text-white/75">
                      {formatCurrencyAmount(currencySymbol, Number(t.min_bet), currencyCode)} – {formatCurrencyAmount(currencySymbol, Number(t.max_bet), currencyCode)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-bold uppercase tracking-wider text-white/35">Blind rounds</dt>
                    <dd className="font-semibold text-white/75">{t.max_blind_rounds ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-bold uppercase tracking-wider text-white/35">Seats</dt>
                    <dd className="flex items-center gap-1.5 font-semibold text-white/75">
                      <Users size={14} className="text-tp-gold/70" />
                      {Number(t.seated_players)}/{t.max_seats}
                    </dd>
                  </div>
                  {Number(t.shar_amount) > 0 && (
                    <div>
                      <dt className="text-[10px] font-bold uppercase tracking-wider text-white/35">Shar</dt>
                      <dd className="font-semibold tabular-nums text-white/75">
                        {formatCurrencyAmount(currencySymbol, Number(t.shar_amount), currencyCode)}
                      </dd>
                    </div>
                  )}
                </dl>
                <p className="mt-3 text-xs text-white/40">
                  You join this stakes tier — the server seats you at the best open table for this profile.
                </p>
                <button
                  type="button"
                  className="game-play-btn mt-4 disabled:cursor-not-allowed disabled:opacity-45"
                  disabled={isFull || isJoining || joinLoading || !canAfford}
                  onClick={() => void handleTakeSeat(t)}
                >
                  <Zap size={16} />
                  {isJoining ? 'Joining…' : isFull ? 'Table full' : isRejoin ? 'Rejoin table' : !canAfford ? 'Insufficient balance' : 'Select & join'}
                </button>
              </article>
            );
          })}
        </div>
      )}

      <PrivateRoomLobbyCard variantCode={variantParam} onOpen={() => setPrivateModalOpen(true)} />

      <PrivateRoomModal
        open={privateModalOpen}
        variantCode={variantParam}
        initialJoinCode={pendingRoomCode}
        autoJoin={Boolean(pendingRoomCode)}
        onClose={() => setPrivateModalOpen(false)}
        onError={setError}
      />

    </div>
  );
}
